/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { dashboardsDuplicate } from "../funcs/dashboardsDuplicate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Dashboard } from "../models/components/dashboard.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DuplicateDashboardRequest,
  DuplicateDashboardSecurity,
} from "../models/operations/duplicatedashboard.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DuplicateDashboardMutationVariables = {
  request: DuplicateDashboardRequest;
  security?: DuplicateDashboardSecurity | undefined;
  options?: RequestOptions;
};

export type DuplicateDashboardMutationData = Dashboard;

export type DuplicateDashboardMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * duplicateDashboard dashboards
 *
 * @remarks
 * Copy a dashboard into a new one the caller owns, named "<name> (copy)". Every card's widget is copied into a new saved widget too, named the same way, so the copy is fully independent of the original. Like every widget save, each copy is validated, so a dashboard with a broken widget cannot be duplicated until the widget is fixed.
 */
export function useDuplicateDashboardMutation(
  options?: MutationHookOptions<
    DuplicateDashboardMutationData,
    DuplicateDashboardMutationError,
    DuplicateDashboardMutationVariables
  >,
): UseMutationResult<
  DuplicateDashboardMutationData,
  DuplicateDashboardMutationError,
  DuplicateDashboardMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDuplicateDashboardMutation(client, options),
    ...options,
  });
}

export function mutationKeyDuplicateDashboard(): MutationKey {
  return ["@gram/client", "dashboards", "duplicate"];
}

export function buildDuplicateDashboardMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DuplicateDashboardMutationVariables,
  ) => Promise<DuplicateDashboardMutationData>;
} {
  return {
    mutationKey: mutationKeyDuplicateDashboard(),
    mutationFn: function duplicateDashboardMutationFn({
      request,
      security,
      options,
    }): Promise<DuplicateDashboardMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(dashboardsDuplicate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
